// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
// A failed action answers with whatever its handler wrote, which for a
// gateway or proxy failure can be an HTML page or a stack trace. Only the
// first line is offered to a form, and only as much of it as a notification
// can reasonably show.
var MAX_REASON_LENGTH = 200;
var MAX_MESSAGE_LENGTH = 4000;
export var parseActionResponseBody = function (body) {
    if (!body) {
        return undefined;
    }
    try {
        return JSON.parse(body);
    }
    catch (_a) {
        return undefined;
    }
};
var readableReason = function (value) {
    if (typeof value !== "string") {
        return undefined;
    }
    var firstLine = value.split("\n")[0].trim();
    if (firstLine.length === 0) {
        return undefined;
    }
    return firstLine.length > MAX_REASON_LENGTH
        ? "".concat(firstLine.slice(0, MAX_REASON_LENGTH), "...")
        : firstLine;
};
var readableMessage = function (value) {
    if (typeof value !== "string") {
        return undefined;
    }
    var message = value.trim();
    if (message.length === 0) {
        return undefined;
    }
    return message.length > MAX_MESSAGE_LENGTH
        ? "".concat(message.slice(0, MAX_MESSAGE_LENGTH), "...")
        : message;
};
/**
 * Whether a failed action carries `errorCode`, wherever Hasura left it: the
 * promoted extension code when it parsed Harvest's answer, the original body
 * when it could not, or one of `legacyMessages` from backends that answered
 * with a message only.
 */
export var hasGraphQLActionErrorCode = function (error, errorCode, legacyMessages) {
    var _a;
    if (legacyMessages === void 0) { legacyMessages = []; }
    var actionError = error;
    var markers = __spreadArray([errorCode], legacyMessages, true);
    var containsMarker = function (value) {
        return markers.some(function (marker) { return (value === null || value === void 0 ? void 0 : value.includes(marker)) === true; });
    };
    return (((_a = actionError === null || actionError === void 0 ? void 0 : actionError.graphQLErrors) === null || _a === void 0 ? void 0 : _a.some(function (graphQLError) {
        var _a, _b, _c, _d;
        return ((_a = graphQLError.extensions) === null || _a === void 0 ? void 0 : _a.code) === errorCode ||
            containsMarker(graphQLError.message) ||
            containsMarker((_d = (_c = (_b = graphQLError.extensions) === null || _b === void 0 ? void 0 : _b.internal) === null || _c === void 0 ? void 0 : _c.response) === null || _d === void 0 ? void 0 : _d.body);
    })) === true || containsMarker(actionError === null || actionError === void 0 ? void 0 : actionError.message));
};
/**
 * Extracts why a Hasura action failed, so a form can tell the admin what went
 * wrong instead of only that something did.
 *
 * Harvest answers a failed action with `{message, extensions: {code}}`. Hasura
 * forwards that message as the GraphQL error message, but when it cannot parse
 * the handler's answer it reports a generic message of its own and keeps the
 * original body in `extensions.internal.response.body`, so the body is read
 * first. Transport failures (a timeout, for instance) carry no body at all and
 * only describe themselves in `extensions.internal.error`.
 */
export var getGraphQLActionErrorReason = function (error) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    var actionError = error;
    var graphQLErrors = Array.isArray(actionError === null || actionError === void 0 ? void 0 : actionError.graphQLErrors) ? actionError.graphQLErrors : [];
    for (var _i = 0, graphQLErrors_1 = graphQLErrors; _i < graphQLErrors_1.length; _i++) {
        var graphQLError = graphQLErrors_1[_i];
        var responseBody = parseActionResponseBody((_c = (_b = (_a = graphQLError.extensions) === null || _a === void 0 ? void 0 : _a.internal) === null || _b === void 0 ? void 0 : _b.response) === null || _c === void 0 ? void 0 : _c.body);
        var reason = (_h = (_d = readableReason(responseBody === null || responseBody === void 0 ? void 0 : responseBody.message)) !== null && _d !== void 0 ? _d : readableReason((_g = (_f = (_e = graphQLError.extensions) === null || _e === void 0 ? void 0 : _e.internal) === null || _f === void 0 ? void 0 : _f.error) === null || _g === void 0 ? void 0 : _g.message)) !== null && _h !== void 0 ? _h : readableReason(graphQLError.message);
        if (reason) {
            return reason;
        }
    }
    return readableReason(actionError === null || actionError === void 0 ? void 0 : actionError.message);
};
/**
 * Extracts the complete action message for an inline error panel. Unlike the
 * compact notification reason above, this preserves line breaks and multiple
 * validation reasons while still bounding untrusted webhook output.
 */
export var getGraphQLActionErrorMessage = function (error) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    var actionError = error;
    var graphQLErrors = Array.isArray(actionError === null || actionError === void 0 ? void 0 : actionError.graphQLErrors) ? actionError.graphQLErrors : [];
    for (var _i = 0, graphQLErrors_2 = graphQLErrors; _i < graphQLErrors_2.length; _i++) {
        var graphQLError = graphQLErrors_2[_i];
        var responseBody = parseActionResponseBody((_c = (_b = (_a = graphQLError.extensions) === null || _a === void 0 ? void 0 : _a.internal) === null || _b === void 0 ? void 0 : _b.response) === null || _c === void 0 ? void 0 : _c.body);
        var message = (_h = (_d = readableMessage(responseBody === null || responseBody === void 0 ? void 0 : responseBody.message)) !== null && _d !== void 0 ? _d : readableMessage((_g = (_f = (_e = graphQLError.extensions) === null || _e === void 0 ? void 0 : _e.internal) === null || _f === void 0 ? void 0 : _f.error) === null || _g === void 0 ? void 0 : _g.message)) !== null && _h !== void 0 ? _h : readableMessage(graphQLError.message);
        if (message) {
            return message;
        }
    }
    return readableMessage(actionError === null || actionError === void 0 ? void 0 : actionError.message);
};
/** Promote only timezone guard reasons; arbitrary PostgreSQL details stay private. */
export var getTimeZoneValidationMessage = function (error) {
    var objectOf = function (value) {
        return value !== null && typeof value === "object" ? value : {};
    };
    var extensions = objectOf(objectOf(error).extensions);
    var details = objectOf(objectOf(extensions.internal).error);
    var message = readableReason(details.message);
    if (details.status_code === "55P03") {
        return message === "Election event settings are busy; retry the timezone save"
            ? message
            : undefined;
    }
    if (details.status_code !== "23514" || !message)
        return undefined;
    var prefixes = [
        "Invalid IANA timezone:",
        "Timezones require a configured array",
        "Configured timezones must be IANA zone names",
        "At least one timezone is required; primary must be configured",
        "Log timezone policy must be primary or election",
        "Timezone ",
        "Post timezone ",
        "Election event for Post timezone does not exist",
        "Election event presentation must be an object",
        "Post presentation must be an object",
    ];
    return prefixes.some(function (prefix) { return message.startsWith(prefix); }) ? message : undefined;
};
